import { Grid } from '@backstage/ui';
import { useEntity } from '@backstage/plugin-catalog-react';
import { EntityLayout } from './entity-layout';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { appTranslationRef } from '../../../core/i18n';
import layoutStyles from './layouts.module.css';
import {
  EntityWarningContent,
  AboutCard,
  EntityGraphCard,
  GitLabLanguagesCard,
  GitLabReleasesCard,
  GitLabEnvironmentsCard,
  HasResourcesCard,
  ProvidedApisCard,
  ConsumedApisCard,
  LinksCard,
  QualitySummaryCard,
} from '../components';

/**
 * Tab 1: Overview — 2-column layout
 * Left (md:8): About, Environments, Provided/Consumed APIs (by type), Resources
 * Right (md:4): Languages, Links, Releases, Relations graph
 */
const OverviewContent = () => {
  const { entity } = useEntity();
  const type = (entity.spec as { type?: string })?.type;

  return (
    <Grid.Root columns="12" gap="3" className={layoutStyles.gridStretch}>
      <EntityWarningContent />

      {/* Left column: main info */}
      <Grid.Item colSpan={{ xs: '12', md: '8' }}>
        <Grid.Root columns="1" gap="3" className={layoutStyles.gridStretch}>
          <Grid.Item colSpan="1">
            <AboutCard />
          </Grid.Item>
          <Grid.Item colSpan="1">
            <GitLabEnvironmentsCard />
          </Grid.Item>
          {type === 'web-api' && (
            <Grid.Item colSpan="1">
              <ProvidedApisCard />
            </Grid.Item>
          )}
          {(type === 'web-app' || type === 'mobile-app') && (
            <Grid.Item colSpan="1">
              <ConsumedApisCard />
            </Grid.Item>
          )}
          <Grid.Item colSpan="1">
            <HasResourcesCard />
          </Grid.Item>
        </Grid.Root>
      </Grid.Item>

      {/* Right column: context */}
      <Grid.Item colSpan={{ xs: '12', md: '4' }}>
        <Grid.Root columns="1" gap="3" className={layoutStyles.gridStretch}>
          <Grid.Item colSpan="1">
            <GitLabLanguagesCard />
          </Grid.Item>
          <Grid.Item colSpan="1">
            <LinksCard />
          </Grid.Item>
          <Grid.Item colSpan="1">
            <GitLabReleasesCard />
          </Grid.Item>
          <Grid.Item colSpan="1">
            <EntityGraphCard height={300} />
          </Grid.Item>
          <Grid.Item colSpan="1">
            <QualitySummaryCard />
          </Grid.Item>
        </Grid.Root>
      </Grid.Item>
    </Grid.Root>
  );
};

/**
 * Unified layout for all Component entity types (service, website, etc.)
 *
 * Tabs:
 * 1. Overview - About, languages, relations, environments, releases
 * 2. Diagram - Full-size entity graph
 */
export const ComponentEntityLayout = () => {
  const { t } = useTranslationRef(appTranslationRef);

  return (
    <EntityLayout>
      <EntityLayout.Route path="/" title={t('entity.tabs.overview')}>
        <OverviewContent />
      </EntityLayout.Route>

      <EntityLayout.Route path="/diagram" title={t('entity.tabs.diagram')}>
        <EntityGraphCard
          height={700}
          direction="TB"
          showMinimap
          title={t('entity.componentDiagram')}
        />
      </EntityLayout.Route>
    </EntityLayout>
  );
};
